import { useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ChevronLeft, ChevronRight, Pencil, Printer, Save, Trash2, Truck, Wrench, X } from "lucide-react";
import { toast } from "sonner";

import api, { formatApiError } from "@/lib/api";
import { StatusPill, SectionPill } from "@/components/StatusPill";
import ShipmentDialog from "@/components/customers/ShipmentDialog";
import CustomerCardModal from "@/components/customers/CustomerCardModal";
import CustomerNotesTimeline from "@/components/customers/CustomerNotesTimeline";
import DuplicateCustomerModal from "@/components/customers/DuplicateCustomerModal";
import { Card, DiscountField, Field, Grid } from "@/components/customers/CustomerDetailFields";
import ReferralSourceField from "@/components/customers/ReferralSourceField";
import LanguageField from "@/components/customers/LanguageField";
import useUnsavedChanges from "@/hooks/useUnsavedChanges";
import UnsavedDot from "@/components/UnsavedDot";

const FIELDS = [
  "name", "phone", "email", "tax_id", "discount_pct",
  "address", "postal_code", "city", "province", "country",
  "is_company",
  "billing_name", "billing_tax_id", "billing_address",
  "billing_postal_code", "billing_city", "billing_province", "billing_country",
  "notes",
  "referral_source",  // iter 80
  "language_code",    // iter 156
];

function pickEditable(c) {
  const out = {};
  for (const k of FIELDS) {
    if (k === "is_company") out[k] = c[k] ?? false;
    else if (k === "discount_pct") out[k] = c[k] ?? 0;
    else out[k] = c[k] ?? "";
  }
  return out;
}

function blankForm() {
  const blank = {};
  for (const k of FIELDS) {
    if (k === "is_company") blank[k] = false;
    else if (k === "discount_pct") blank[k] = 0;
    else blank[k] = "";
  }
  return blank;
}

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isCreate = id === "new";
  // Estado inicial via lazy initializer (evita set-state-in-effect en el
  // arranque cuando estamos creando un cliente nuevo).
  const [customer, setCustomer] = useState(() =>
    isCreate ? { id: "new", code: "—", name: "", phone: "", email: "", tax_id: "" } : null
  );
  const [repairs, setRepairs] = useState([]);
  const [editing, setEditing] = useState(isCreate);
  const [form, setForm] = useState(() => (isCreate ? blankForm() : null));
  const [saving, setSaving] = useState(false);
  // Iter 92 — Guardia sincrónica contra doble-click: `setSaving` es asíncrono,
  // por lo que un segundo click puede pasar el check `if (saving)` antes de
  // que React re-renderice. Un ref se actualiza inmediatamente.
  const savingRef = useRef(false);
  const [showShipment, setShowShipment] = useState(false);
  const [showCard, setShowCard] = useState(false);
  const [duplicate, setDuplicate] = useState(null); // { existing, conflictField }
  const [siblings, setSiblings] = useState({ prev: null, next: null });
  // Iter 123 — clave que fuerza la recarga del historial tras acciones de shipment.
  const [notesRefreshKey, setNotesRefreshKey] = useState(0);

  // Detecta cambios sin guardar (modo edición)
  const isDirty = useMemo(() => {
    if (!editing || !customer || !form) return false;
    const baseline = isCreate ? blankForm() : pickEditable(customer);
    return JSON.stringify(baseline) !== JSON.stringify(form);
  }, [editing, customer, form, isCreate]);

  // Carga cliente anterior/siguiente (mismo orden que el listado: created_at desc)
  useEffect(() => {
    if (isCreate || !id) return;
    let cancelled = false;
    api.get("/customers").then((r) => {
      if (cancelled) return;
      const list = r.data || [];
      const idx = list.findIndex((c) => c.id === id);
      if (idx === -1) { setSiblings({ prev: null, next: null }); return; }
      setSiblings({
        prev: idx > 0 ? list[idx - 1] : null,
        next: idx < list.length - 1 ? list[idx + 1] : null,
      });
    }).catch(() => setSiblings({ prev: null, next: null }));
    return () => { cancelled = true; };
  }, [id, isCreate]);

  const goSibling = (target) => {
    if (!target) return;
    navigate(`/customers/${target.id}`);
  };

  const load = async () => {
    try {
      const [c, r] = await Promise.all([
        api.get(`/customers/${id}`),
        api.get(`/customers/${id}/repairs`),
      ]);
      setCustomer(c.data);
      setForm(pickEditable(c.data));
      setRepairs(r.data);
    } catch (err) {
      toast.error(formatApiError(err));
      navigate("/customers");
    }
  };

  useEffect(() => {
    if (isCreate) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const save = async () => {
    if (savingRef.current) return false; // Iter 92 — bloquea reentradas del onClick
    savingRef.current = true;
    setSaving(true);
    try {
      if (isCreate) {
        const r = await api.post("/customers", form);
        toast.success(`Cliente ${r.data.code} creado`);
        // Iter 92 — flushSync fuerza el rerender ANTES de navigate() para que
        // isDirty=false y useUnsavedChanges no bloquee la navegación.
        flushSync(() => {
          setForm(blankForm());
          setEditing(false);
        });
        navigate(`/customers/${r.data.id}`);
        return true;
      }
      const r = await api.put(`/customers/${id}`, form);
      setCustomer(r.data);
      setForm(pickEditable(r.data));
      setEditing(false);
      toast.success("Cliente actualizado");
      return true;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail?.code === "duplicate_customer") {
        setDuplicate({ existing: detail.existing, conflictField: detail.conflict_field });
      } else {
        toast.error(formatApiError(err));
      }
      return false;
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  // Iter 82 — Guardia de cambios sin guardar (navegación interna + cierre de pestaña).
  const unsaved = useUnsavedChanges({ dirty: isDirty, onSaveAndExit: save });

  /** Normaliza en tiempo real lo que el usuario teclea en email / teléfono. */
  const update = (k, v) => {
    let val = v;
    if (k === "email" && typeof v === "string") {
      val = v.replace(/\s+/g, "").toLowerCase();
    } else if (k === "phone" && typeof v === "string") {
      const stripped = v.replace(/\s+/g, "");
      const hasPlus = stripped.startsWith("+");
      const digits = stripped.replace(/[^\d]/g, "");
      val = hasPlus ? "+" + digits : digits;
    }
    setForm({ ...form, [k]: val });
  };

  const cancelEdit = () => {
    if (isCreate) {
      navigate("/customers");
      return;
    }
    setForm(pickEditable(customer));
    setEditing(false);
  };

  const del = async () => {
    if (!window.confirm(`¿Eliminar cliente "${customer.name}"? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/customers/${id}`);
      toast.success("Cliente eliminado");
      navigate("/customers");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (!customer || !form) return <div className="p-10 text-sm text-[#525252]">Cargando ficha...</div>;

  return (
    <div className="p-6 lg:p-8 max-w-[1500px] mx-auto">
      {/* Top bar */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Link
            to="/customers"
            data-testid="customer-back"
            className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-[.14em] text-[#4B5158] hover:text-[#3F5222] font-semibold transition-colors"
          >
            <ArrowLeft size={12} /> Volver al listado
          </Link>
          {!isCreate && (
            <div className="ds-pager ml-3">
              <button
                type="button"
                onClick={() => goSibling(siblings.prev)}
                disabled={!siblings.prev}
                data-testid="customer-prev"
                title={siblings.prev ? `← ${siblings.prev.name}` : "No hay cliente anterior"}
              >
                <ChevronLeft size={12} /> Anterior
              </button>
              <button
                type="button"
                onClick={() => goSibling(siblings.next)}
                disabled={!siblings.next}
                data-testid="customer-next"
                title={siblings.next ? `${siblings.next.name} →` : "No hay cliente siguiente"}
              >
                Siguiente <ChevronRight size={12} />
              </button>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!isCreate && (
            <Link
              to={`/repairs/new?customer=${customer.id}`}
              data-testid="customer-newrepair"
              className="ds-btn"
            >
              <Wrench size={14} /> Nueva reparación
            </Link>
          )}
          {!isCreate && (
            <button
              type="button"
              onClick={() => setShowCard(true)}
              data-testid="customer-card-btn"
              title="Imprimir ficha del cliente"
              className="ds-btn"
            >
              <Printer size={14} /> Ficha Cliente
            </button>
          )}
          {!isCreate && (
            <button
              type="button"
              onClick={() => setShowShipment(true)}
              data-testid="customer-shipment-btn"
              title="Solicitar recogida o envío al cliente"
              className="ds-btn"
            >
              <Truck size={14} /> Recogida / Envío
            </button>
          )}
          {!editing ? (
            <button
              onClick={() => setEditing(true)}
              data-testid="customer-edit"
              className="ds-btn primary"
            >
              <Pencil size={14} /> Editar
            </button>
          ) : (
            <>
              <button
                onClick={cancelEdit}
                data-testid="customer-cancel"
                className="ds-btn"
              >
                <X size={14} /> Cancelar
              </button>
              <button
                onClick={save}
                disabled={saving || (isCreate && !form.name?.trim())}
                data-testid="customer-save"
                className="ds-btn primary"
              >
                <UnsavedDot show={isDirty} />
                <Save size={14} /> {saving ? "Guardando..." : (isCreate ? "Crear cliente" : "Guardar")}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Cabecera */}
      <header className="mt-4">
        <div className="flex items-start justify-between gap-6">
          <div className="min-w-0">
            <div className="font-mono text-[15px] uppercase tracking-widest text-[#6E8B3D]">
              // {isCreate ? "Crear nuevo cliente" : `Ficha de cliente · ${customer.code || "—"}`}
            </div>
            <h1 className="font-display text-3xl md:text-4xl font-black mt-2 truncate" data-testid="customer-name-display">
              {isCreate ? (form.name || "Nuevo cliente") : customer.name}
            </h1>
            {!isCreate && (
              <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm">
                {customer.tax_id && (
                  <span className="text-[#525252]">
                    <span className="font-mono text-[10px] uppercase tracking-widest mr-1">NIF/DNI</span>
                    {customer.tax_id}
                  </span>
                )}
                {customer.phone && (
                  <span className="text-[#525252]">
                    <span className="font-mono text-[10px] uppercase tracking-widest mr-1">Tel</span>
                    {customer.phone}
                  </span>
                )}
                {customer.email && (
                  <span className="text-[#525252]">
                    <span className="font-mono text-[10px] uppercase tracking-widest mr-1">Email</span>
                    {customer.email}
                  </span>
                )}
              </div>
            )}
            {isCreate && (
              <p className="mt-3 text-sm text-[#525252]">
                Rellena los datos y pulsa <span className="font-semibold text-[#111110]">Crear cliente</span>. El código se asignará automáticamente.
              </p>
            )}
          </div>
          {!isCreate && (
            <div className="text-right shrink-0">
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">
                Reparaciones
              </div>
              <div className="font-display text-4xl font-black">{repairs.length}</div>
              <button
                onClick={del}
                data-testid="customer-delete"
                className="mt-3 inline-flex items-center gap-2 text-xs text-red-700 hover:text-red-900"
              >
                <Trash2 size={12} /> Eliminar cliente
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Grid: contacto + dirección | facturación + notas */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Datos personales / contacto">
          <div className="space-y-3" data-testid="customer-contact-section">
            {/* Fila 1: Nombre · NIF/DNI · DTO */}
            <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,110px)] gap-3">
              <Field label="Nombre / Razón social *" required value={form.name} onChange={(v) => update("name", v)} editing={editing} testid="customer-field-name" />
              <Field label="NIF / DNI" value={form.tax_id} onChange={(v) => update("tax_id", v)} editing={editing} testid="customer-field-taxid" />
              <DiscountField
                value={form.discount_pct}
                onChange={(v) => update("discount_pct", v)}
                editing={editing}
                testid="customer-field-discount"
              />
            </div>
            {/* Fila 2: Teléfono · Email */}
            <Grid>
              <Field label="Teléfono" value={form.phone} onChange={(v) => update("phone", v)} editing={editing} type="tel" testid="customer-field-phone" />
              <Field label="Email (varios separados por , o ;)" value={form.email} onChange={(v) => update("email", v)} editing={editing} type="text" testid="customer-field-email" />
            </Grid>
            {/* Fila 3: ¿Cómo nos has conocido? · Idioma */}
            <Grid>
              <ReferralSourceField
                value={form.referral_source}
                onChange={(v) => update("referral_source", v)}
                editing={editing}
                required={isCreate}
              />
              <LanguageField
                value={form.language_code}
                onChange={(v) => update("language_code", v)}
                editing={editing}
              />
            </Grid>
          </div>
        </Card>

        <Card title="Dirección">
          <Grid>
            <Field label="Dirección" value={form.address} onChange={(v) => update("address", v)} editing={editing} span={2} testid="customer-field-address" />
            <Field label="Código postal" value={form.postal_code} onChange={(v) => update("postal_code", v)} editing={editing} />
            <Field label="Población" value={form.city} onChange={(v) => update("city", v)} editing={editing} testid="customer-field-city" />
            <Field label="Provincia" value={form.province} onChange={(v) => update("province", v)} editing={editing} />
            <Field label="País" value={form.country} onChange={(v) => update("country", v)} editing={editing} />
          </Grid>
        </Card>

        <Card title="Datos de facturación">
          <label className="flex items-center gap-2 mb-3">
            <input
              type="checkbox"
              checked={!!form.is_company}
              disabled={!editing}
              onChange={(e) => update("is_company", e.target.checked)}
              data-testid="customer-field-iscompany"
              className="h-4 w-4 accent-[#6E8B3D]"
            />
            <span className="text-sm font-medium">Facturar a nombre de empresa</span>
          </label>
          {form.is_company ? (
            <Grid>
              <Field label="Nombre fiscal / Razón social" value={form.billing_name} onChange={(v) => update("billing_name", v)} editing={editing} span={2} testid="customer-field-billingname" />
              <Field label="NIF / CIF de empresa" value={form.billing_tax_id} onChange={(v) => update("billing_tax_id", v)} editing={editing} span={2} testid="customer-field-billingtaxid" />
              <Field label="Dirección fiscal" value={form.billing_address} onChange={(v) => update("billing_address", v)} editing={editing} span={2} />
              <Field label="Código postal fiscal" value={form.billing_postal_code} onChange={(v) => update("billing_postal_code", v)} editing={editing} />
              <Field label="Población fiscal" value={form.billing_city} onChange={(v) => update("billing_city", v)} editing={editing} />
              <Field label="Provincia fiscal" value={form.billing_province} onChange={(v) => update("billing_province", v)} editing={editing} />
              <Field label="País fiscal" value={form.billing_country} onChange={(v) => update("billing_country", v)} editing={editing} />
            </Grid>
          ) : (
            <p className="text-sm text-[#525252]">
              El cliente está marcado como <span className="font-semibold text-[#111110]">particular</span>. Las facturas se emitirán con los datos personales.
            </p>
          )}
        </Card>

        <Card title="Notas sobre este cliente">
          <textarea
            value={form.notes || ""}
            disabled={!editing}
            onChange={(e) => update("notes", e.target.value)}
            rows={7}
            placeholder="Cliente habitual, preferencias, incidencias anteriores, avisos importantes, comentarios internos del SAT..."
            data-testid="customer-field-notes"
            className="w-full ds-card focus:border-[#6E8B3D] focus:ring-1 focus:ring-[#6E8B3D] outline-none px-3 py-2 text-sm rounded-none disabled:bg-[#F7F7F4]"
          />
        </Card>
      </div>

      {/* Reparaciones anteriores */}
      {!isCreate && (
        <section className="mt-6 ds-card">
          <div className="px-4 py-3 border-b border-[#E5E5E0] flex items-center justify-between">
            <div className="font-display font-bold">Reparaciones anteriores</div>
            <span className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">
              {repairs.length} en total
            </span>
          </div>
          {repairs.length === 0 ? (
            <div className="px-4 py-12 text-center text-sm text-[#525252]" data-testid="repairs-empty">
              Este cliente todavía no tiene reparaciones registradas.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-[#525252] border-b border-[#E5E5E0]">
                  <th className="px-4 py-2.5">Nº reparación</th>
                  <th className="px-4 py-2.5">Fecha</th>
                  <th className="px-4 py-2.5">Dispositivo</th>
                  <th className="px-4 py-2.5">Modelo</th>
                  <th className="px-4 py-2.5">Número de serie</th>
                  <th className="px-4 py-2.5">Sección</th>
                  <th className="px-4 py-2.5">Estado</th>
                </tr>
              </thead>
              <tbody>
                {repairs.map((r) => (
                  <tr
                    key={r.id}
                    onClick={() => navigate(`/repairs/${r.id}`)}
                    className="border-b border-[#E5E5E0] last:border-0 hover:bg-[#F7F7F4] cursor-pointer"
                    data-testid={`customer-repair-${r.code}`}
                  >
                    <td className="px-4 py-2.5 font-mono text-xs">{r.code}</td>
                    <td className="px-4 py-2.5 text-[#525252] font-mono text-xs">
                      {new Date(r.created_at).toLocaleDateString("es")}
                    </td>
                    <td className="px-4 py-2.5">
                      {r.device_type} {r.brand && `· ${r.brand}`}
                    </td>
                    <td className="px-4 py-2.5 text-[#525252]">{r.model || "—"}</td>
                    <td className="px-4 py-2.5 text-[#525252] font-mono text-xs">{r.serial || "—"}</td>
                    <td className="px-4 py-2.5" data-testid={`customer-repair-section-${r.code}`}>
                      <SectionPill section={r.section} status={r.status} />
                    </td>
                    <td className="px-4 py-2.5"><StatusPill status={r.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}

      {/* Iter 123 — Historial del cliente (notas manuales + acciones automáticas) */}
      {!isCreate && (
        <CustomerNotesTimeline customerId={customer.id} refreshKey={notesRefreshKey} />
      )}

      {showShipment && customer && !isCreate && (
        <ShipmentDialog
          customer={customer}
          onClose={() => setShowShipment(false)}
          onLogged={() => setNotesRefreshKey((k) => k + 1)}
        />
      )}

      {showCard && customer && !isCreate && (
        <CustomerCardModal
          customer={customer}
          onClose={() => setShowCard(false)}
        />
      )}

      {duplicate && (
        <DuplicateCustomerModal
          existing={duplicate.existing}
          conflictField={duplicate.conflictField}
          onClose={() => setDuplicate(null)}
        />
      )}

      {/* Iter 82 — Diálogo de cambios sin guardar */}
      {unsaved.dialog}
    </div>
  );
}
